import {
  ComponentLayoutThumbnail,
  ComponentOverlayLayer,
  type ExpectedComponent,
  TimingThumbnail,
} from '@cx-web/components/page-template/ComponentOverlay';
import { FrameworkOverlay } from '@cx-web/components/page-template/FrameworkOverlay';
import clsx from 'clsx';

import { type ComponentLoaderContext } from '~/types';
import { type SectionMap, type SportsPageHubSectionName } from '~/types/template';

import { renderComponents } from '../../utils/renderComponents';
import { Section } from '../Section/Section';

import styles from './SportsPageHubTemplate.module.css';

interface SportsPageHubTemplateProps {
  componentLoaderContext: ComponentLoaderContext;
  expectedComponentLayout?: ExpectedComponent[];
  gtmId: string;
  hasBannerAds: boolean;
  sections: Pick<SectionMap, SportsPageHubSectionName>;
  timings?: Record<string, number>;
}

export const SportsPageHubTemplate = ({
  componentLoaderContext,
  expectedComponentLayout,
  gtmId,
  hasBannerAds,
  sections,
  timings,
}: SportsPageHubTemplateProps) => {
  const showTop = !!sections.top;
  const showCenterTop = !!sections.centerTop;
  const showRight = !!sections.right;
  const showCenterBottom = !!sections.centerBottom;
  const showBottom = !!sections.bottom;

  const containerClasses = clsx('adverts-and-atlas-wrapper', {
    'adverts-and-atlas-wrapper--adless': !hasBannerAds,
  });

  return (
    <div className={containerClasses}>
      <main className={styles.mainWrapper} id="sports-page-hub-main-tag-wrapper">
        {hasBannerAds && (
          <div className="ad-left-container">
            <div id="ad-left"></div>
          </div>
        )}
        {showTop && (
          <Section
            ariaLabel="Page Header Top Section"
            content={renderComponents({
              components: sections.top ?? null,
              componentLoaderContext,
              gtmId,
              section: 'Top',
            })}
            dataTestId="sports-page-hub-top"
            id="sports-page-hub-top"
          />
        )}
        <div
          className={`${styles.sportsPageHubContentContainer} ${showRight ? styles.standardWithRight : styles.standard}`}
          id="sports-page-hub-content-container"
        >
          {showCenterTop && (
            <Section
              ariaLabel="Main Content Center Top Section"
              className={styles.sportsPageHubCenterTop}
              content={renderComponents({
                components: sections.centerTop ?? null,
                componentLoaderContext,
                gtmId,
                section: 'Center Top',
              })}
              dataTestId="sports-page-hub-center-top"
              id="sports-page-hub-center-top"
            />
          )}
          {showRight && (
            <Section
              ariaLabel="Secondary Content Right Section"
              className={styles.sportsPageHubRight}
              content={renderComponents({
                components: sections.right ?? null,
                componentLoaderContext,
                gtmId,
                section: 'Right',
              })}
              dataTestId="sports-page-hub-right"
              id="sports-page-hub-right"
            />
          )}
          {showCenterBottom && (
            <Section
              ariaLabel="Main Content Center Bottom Section"
              className={styles.sportsPageHubCenterBottom}
              content={renderComponents({
                components: sections.centerBottom ?? null,
                componentLoaderContext,
                gtmId,
                section: 'Center Bottom',
              })}
              dataTestId="sports-page-hub-center-bottom"
              id="sports-page-hub-center-bottom"
            />
          )}
        </div>
        {showBottom && (
          <Section
            ariaLabel="Informational Content Bottom Section"
            className={styles.sportsPageHubBottom}
            content={renderComponents({
              components: sections.bottom ?? null,
              componentLoaderContext,
              gtmId,
              section: 'Bottom',
            })}
            dataTestId="sports-page-hub-bottom"
            id="sports-page-hub-bottom"
          />
        )}
        {hasBannerAds && (
          <div className="ad-right-container">
            <div id="ad-right"></div>
          </div>
        )}
        {componentLoaderContext.showFrameworkOverlay && <FrameworkOverlay framework="RRv7" />}
        {componentLoaderContext.showComponentOverlay && <ComponentOverlayLayer />}
        {componentLoaderContext.showComponentOverlay && expectedComponentLayout && (
          <ComponentLayoutThumbnail expectedLayout={expectedComponentLayout} />
        )}
        {timings && <TimingThumbnail timings={timings} />}
      </main>
    </div>
  );
};
